import { ImageMetadata, LinkMetadata } from '@lukso/lsp4-contracts'
import { Verification } from '@lukso/lsp2-contracts'

import { Restriction } from '@/shared/models/Restriction'
import { Permissions, PermissionWhitelist } from '@/shared/Permissions'
import {
  NetworkInfoDBSchema,
  RelayServiceDBSchema,
} from '@/shared/Database/UPSettings/models'
import { AuthenticatedApp } from '@/shared/models/AuthenticatedApp'
import {
  AddRelayServiceUseCasePropsDTO,
  UpdateRelayServiceUseCasePropsDTO,
} from '@/business-logic/relay-service'

export type Profile = {
  name?: string
  links?: LinkMetadata[]
  description?: string
  profileImage?: ImageMetadata[]
  backgroundImage?: ImageMetadata[]
  profilePicture?: string
  profilePictureOrientation?: number
  coverPictureDirty?: boolean
  profilePictureDirty?: boolean
  coverPicture?: string
  coverPictureOrientation?: number
  tags?: string[]
  address: UpAddress
  controllerAddress: EoaAddress
  controllerDirty: boolean
  salt?: string
  email?: string
  latestBlock?: number
  oldestBlock?: number
  verification?: Verification
  importStatus?: ImportStatus
  restoreStatus?: 'restored'
  gasProviderId?: RelayServiceDBSchema['id']
}

export type FrontendProfile = Pick<
  Profile,
  | 'address'
  | 'name'
  | 'links'
  | 'description'
  | 'profileImage'
  | 'profilePictureOrientation'
  | 'backgroundImage'
  | 'profilePicture'
  | 'coverPictureDirty'
  | 'profilePictureDirty'
  | 'coverPicture'
  | 'coverPictureOrientation'
  | 'tags'
  | 'verification'
  | 'importStatus'
  | 'restoreStatus'
  | 'gasProviderId'
>

/**
 * Import origin DApp is used once the import transaction is completed to create a connection between the origin DApp that
 * sent the original up_import RPC and the extension.
 */
export type ImportOriginDapp = Pick<
  AuthenticatedApp,
  'origin' | 'name' | 'icon'
>

export type BackgroundProfile = Pick<
  Profile,
  | 'address'
  | 'controllerAddress'
  | 'controllerDirty'
  | 'latestBlock'
  | 'importStatus'
  | 'restoreStatus'
  | 'oldestBlock'
  | 'verification'
> & { importOriginDapp?: ImportOriginDapp }

export type ControllerDetailsEdit = {
  value?: string
  isExecuteOpen?: boolean
  isSetDataOpen?: boolean
  isExecutePermissionsOpen?: boolean
  activeExecutePermission?: PermissionWhitelist
}

export enum Tab {
  ACTIVITY = 'ACTIVITY',
  AUTHENTICATED = 'AUTHENTICATED',
  AUTHORIZED = 'AUTHORIZED',
}

export type ImportStatus =
  /**
   * An LSP23 profile address and UP browser extension controller has been created, but the profile is not yet onchain.
   */
  | 'initializing'
  /**
   * Then profile is onchain and a UP browser extension controller has been created, but the controller is not yet
   * assigned onchain.
   */
  | 'importing'
  /**
   * The profile has been created onchain and the UP browser extension controller has control of the profile.
   */
  | 'imported'

export type ImportRequestInput = {
  address: UpAddress
  controllerAddress?: string
  importStatus?: ImportStatus
  errorMessage?: string
  /**
   * DApp related fields
   */
  icon?: string
  name?: string
}

export type HomeEdit = {
  tab?: Tab
}

export type ImportQrCodeScanner = {
  qrCodeTextValue?: string
  showQrCodeText?: boolean
}

export type ChangePasswordStates = {
  existingPassword?: string
  newPassword?: string
}

export type LastPopupState = {
  profileAddress: string
  timestamp: number
  path: string
  editProfile?: FrontendProfile
  homeEdit?: HomeEdit
  restrictions?: Restriction[]
  permissions?: Permissions
  originalPermissions?: Permissions
  appDetails?: ControllerDetailsEdit
  network?: NetworkInfoDBSchema
  networkRelayers?: string
  relayer?: AddRelayServiceUseCasePropsDTO | UpdateRelayServiceUseCasePropsDTO
  selectedChainIds?: string
  importQrCodeScanner?: ImportQrCodeScanner
  changePassword?: ChangePasswordStates
}

export type SeenPermissionChanges = {
  profileAddress: string
  hasSeenPermissionsChangeWarning?: boolean
}

export type DigitalAsset = {
  assetAddress: string
  icon?: string
  name?: string
  symbol?: string
  images?: string[]
  description?: string
  links?: LinkMetadata[]
}

export type PopupProfile = {
  address: UpAddress
  temporary?: FrontendProfile
  saved?: FrontendProfile
  isOwner?: boolean
  createdAt?: number
}

export type ProfileList = { [K in string]: PopupProfile }

export type PopupStorage = {
  profiles: ProfileList
}

export type DropdownProfile = Pick<
  Profile,
  'address' | 'name' | 'profilePicture' | 'verification'
> & { createdAt?: number }

export type UpdateProfile = Profile &
  Required<Pick<Profile, 'address' | 'controllerAddress'>>

export type ProfilePictures = Partial<{
  coverPicture: string
  profilePicture: string
}>

export type UpAddress = string
export type EoaAddress = string
export type KmAddress = string
export type ContractAddress = string
export type SeedId = string | number

export const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'

export const NO_PERMISSIONS =
  '0x0000000000000000000000000000000000000000000000000000000000000000'

export const ADD_CONTROLLER_ONLY_PERMISSION =
  '0x0000000000000000000000000000000000000000000000000000000000000002'

export type LinkMetadataSocialMedia = LinkMetadata & {
  socialMediaName?: string
}
